// Данные для разделов «Кабинет закупщика» и «Отгрузка».
//
// Два источника, и они принципиально разные:
//   • заявки на оплату — смарт-процесс 1160 в ядре (POST /api/bx, эмуляция Bitrix REST);
//   • отгрузка и заказы поставщику — 1С УТ через кеширующий прокси ядра (GET /api/1c/ut/…).
//
// ПОЧЕМУ ЗДЕСЬ СВОЙ ЗАПРОС К /api/bx, А НЕ api/client.ts.
// Ядро с BFF отдают в ответе пометку `_scope` — «выборка сужена по вашей зоне ответственности»
// и, отдельно, `partial` — «сужена ЗАВЕДОМО неполно». Общий разборщик списков в client.ts
// оставляет от ответа только items/total/next: дебиторке хватало, а закупщику нет. Пометка
// «показано не всё» про деньги, которые компания должна поставщикам, обязана доходить до
// экрана, поэтому транспорт здесь свой — ровно на один метод и с той же обработкой ошибок
// (классы Unauthorized / ApiError / CoreUnsupported переиспользуем, а не заводим свои).
//
// ТОЛЬКО ЧТЕНИЕ. Ни одной записи: 1160 наполняют бизнес-процессы Bitrix, а у прокси к 1С
// записи нет вовсе (см. core_api.py, «кеширующий прокси к 1С»).
import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { ApiError, CoreUnsupported, Unauthorized } from './client'
import { OdataDown, mergeMeta, odataGet, odataRetry } from './odata'
import type { OdataCode, OdataMeta } from './odata'
import type { CrmItem, ListParams } from './types'
import {
  DOC_LIMIT, ORDER_SELECT, PUR_TYPE_ID, SHIPMENT_SELECT, SUPPLIER_SELECT, contragentSearchPath,
  docWindowPath, emptyUtNames, refNamePaths,
} from '../lib/purchasing'
import type { UtNames, UtOrderRaw, UtShipmentRaw, UtSupplierOrderRaw } from '../lib/purchasing'
import { odataPath } from '../lib/stock'
import type { RefName } from '../lib/stock'

// ═════════════════════════ смарт-процесс 1160 через ядро ═════════════════════════

/**
 * Пометка ограничения выборки из BFF (`_scope`).
 * `limited` — выборка сужена по зоне; `note` — почему она заведомо неполная (пусто, если
 * сужение точное). См. web/bff/rights.py и ПРАВА_ДОСТУПА.md.
 */
export interface ScopeMark {
  limited: boolean
  partial: boolean
  note: string
}

export interface PayListResult {
  items: CrmItem[]
  total: number
  next?: number
  /** Ядро отдало пометку «синк отстал» — в ответе НЕТ result вовсе. */
  stale?: boolean
  ageMin?: number
  /** Права сузили выборку. undefined — выборка полная. */
  scope?: ScopeMark
}

interface BxAnswer {
  result?: { items?: CrmItem[] }
  total?: number
  next?: number
  _stale?: boolean
  age_min?: number
  _scope?: { limited?: boolean; partial?: boolean; note?: string }
  _core_unsupported?: boolean
  why?: string
  error?: string
  detail?: string
}

/**
 * Один вызов `crm.item.list` по типу 1160 — своим запросом, чтобы не потерять `_scope`.
 * Обработка ответов повторяет client.ts: 401 → Unauthorized, `_core_unsupported` → отдельный
 * тип ошибки (пустая таблица на месте отказа читалась бы как «заявок нет»).
 */
async function fetchPayList(params: ListParams): Promise<PayListResult> {
  const res = await fetch('/api/bx', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ method: 'crm.item.list', params: { entityTypeId: PUR_TYPE_ID, ...params } }),
  })
  if (res.status === 401) throw new Unauthorized()

  const text = await res.text()
  let data: BxAnswer
  try {
    data = text ? (JSON.parse(text) as BxAnswer) : {}
  } catch {
    throw new ApiError(`ответ ядра не разобрался как JSON: ${text.slice(0, 160)}`, res.status)
  }
  if (data._core_unsupported) throw new CoreUnsupported(String(data.why ?? ''))
  if (!res.ok) throw new ApiError(data.error || data.detail || `HTTP ${res.status}`, res.status)

  const scope: ScopeMark | undefined = data._scope
    ? {
        limited: !!data._scope.limited,
        partial: !!data._scope.partial,
        note: String(data._scope.note ?? ''),
      }
    : undefined

  // `_stale` означает, что result в ответе отсутствует вовсе: показывать пустой список
  // как «заявок нет» нельзя, экран обязан вывести предупреждение.
  if (data._stale) return { items: [], total: 0, stale: true, ageMin: data.age_min, scope }

  const items = data.result?.items ?? []
  return { items, total: data.total ?? items.length, next: data.next, scope }
}

/** Отказ ядра и потерянную сессию не повторяем: ответ детерминированный. */
const retry = (count: number, err: unknown) =>
  !(err instanceof Unauthorized) && !(err instanceof CoreUnsupported) && count < 2

/** Список заявок на оплату поставщику. */
export const usePayRequests = (params: ListParams) =>
  useQuery({
    queryKey: ['pay-requests', params],
    queryFn: () => fetchPayList(params),
    retry,
    staleTime: 30_000,
    placeholderData: (prev: PayListResult | undefined) => prev,
  })

/**
 * Счётчик заявок по одному условию. Данные не нужны — нужен только `total`, который ядро
 * считает отдельным `count(*)`, поэтому просим один id и одну запись.
 *
 * Почему счётчики отдельными запросами, а не подсчётом по загруженной странице: агрегатов
 * ядро не отдаёт, а «ждёт согласования» по странице из пятидесяти строк — это не число,
 * а случайность. Пять условий против 11 046 записей (на 28.07.2026) ядро считает быстро,
 * и react-query держит результат минуту.
 */
export const usePayCount = (filter: Record<string, unknown>, enabled = true) =>
  useQuery({
    queryKey: ['pay-count', filter],
    queryFn: () => fetchPayList({ filter, select: ['id'] }),
    enabled,
    retry,
    staleTime: 60_000,
  })

// ═════════════════════════ 1С УТ: документы отгрузки и закупки ═════════════════════════

/** Ответ ядра на пачку документов: сами строки, их общее число в окне и свежесть. */
interface DocsResult<T> {
  rows: T[]
  /** Сколько документов в окне ВСЕГО (из $inlinecount), а не сколько влезло в DOC_LIMIT. */
  totalInWindow: number
  /** true — окно не влезло в DOC_LIMIT, показана только его часть. */
  truncated: boolean
  meta?: OdataMeta
}

/**
 * Пачка документов из 1С.
 *
 * ПОЧЕМУ СВОЙ ЗАПРОС, А НЕ odataGet. Общий `odataGet` оставляет от ответа 1С только `value`
 * и свежесть, а нам нужен ещё `odata.count` — сколько документов в окне ВСЕГО. Без него
 * «показано 500» невозможно отличить от «в окне ровно 500», то есть человек не узнает,
 * что видит обрезанную выборку. Разбор отказа повторяет odataGet один в один (тот же
 * тип ошибки OdataDown, те же служебные поля свежести), иначе OdataState не смог бы
 * объяснить человеку, что именно случилось с 1С.
 */
async function fetchDocs<T>(path: string): Promise<DocsResult<T>> {
  const res = await fetch(`/api/1c/ut/${path}`, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()

  const text = await res.text()
  let body: Record<string, unknown>
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {}
  } catch {
    throw new OdataDown('upstream', `ответ не разобрался как JSON: ${text.slice(0, 160)}`, res.status)
  }
  if (!res.ok) {
    const code = typeof body.code === 'string' ? (body.code as OdataCode) : 'unknown'
    const msg = typeof body.error === 'string' ? body.error : `HTTP ${res.status}`
    throw new OdataDown(code, msg, res.status)
  }

  const rows = Array.isArray(body.value) ? (body.value as T[]) : []
  // 1С отдаёт odata.count строкой; ключ с точкой — читаем по индексу, а не через точку
  const count = Number(body['odata.count'])
  const cache = body._cache
  const meta: OdataMeta = {
    cache: cache === 'hit' || cache === 'stale' ? cache : 'miss',
    ageSec: Number(body._age_sec) || 0,
    ttlSec: Number(body._ttl_sec) || 0,
    stale: !!body._stale,
    error: typeof body._1c_error === 'string' ? body._1c_error : undefined,
  }
  return {
    rows,
    totalInWindow: Number.isFinite(count) ? count : rows.length,
    truncated: rows.length >= DOC_LIMIT,
    meta,
  }
}

// Справочники, которые берём ЦЕЛИКОМ одним запросом: их мало, и это дешевле, чем добирать
// названия пачками по 12 ссылок на каждой странице таблицы.
// На 28.07.2026 это 153 склада, 1435 сделок 1С и 119 пользователей:
//   • склады в ядре живут 6 часов;
//   • сделки 1С — единственный мост к номерам сделок Bitrix;
//   • пользователи 1С — это менеджеры в документах.
// Контрагентов и партнёров по 6900 — их целиком не тянем, названия берём по ссылкам
// показанной страницы (см. useUtNamesForPage).
// Путь к складам совпадает с тем, что просит раздел «Склад» (api/stock.ts) — до символа.
// Это не совпадение: у react-query ключ кэша строится из пути, и одинаковый путь означает
// один запрос в 1С на оба раздела вместо двух.
const WAREHOUSES_PATH = odataPath('Catalog_Склады', { $select: 'Ref_Key,Description', $top: 200 })
const UT_DEALS_PATH = odataPath('Catalog_СделкиСКлиентами', { $select: 'Ref_Key,Description', $top: 2000 })
const UT_USERS_PATH = odataPath('Catalog_Пользователи', { $select: 'Ref_Key,Description', $top: 200 })

function refMap(rows: RefName[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const r of rows) if (r.Ref_Key) out[r.Ref_Key] = r.Description || ''
  return out
}

/** Один справочник целиком, через общий кэш react-query: два экрана делят один запрос. */
function fetchDict(qc: QueryClient, path: string, staleTime: number) {
  return qc.fetchQuery({
    queryKey: ['1c', 'ut', path, null],
    queryFn: () => odataGet<RefName>('ut', path),
    staleTime,
  })
}

export interface UtDicts {
  names: UtNames
  meta?: OdataMeta
  /** Какие справочники не пришли — экран об этом пишет, а не показывает пустые ячейки. */
  failed: string[]
}

/**
 * Склады, сделки 1С и менеджеры. Ошибка одного справочника не роняет экран: без склада
 * строка отгрузки всё равно полезна, а «—» в колонке честнее пустой таблицы.
 */
export function useUtDicts() {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['ut-dicts'],
    staleTime: 30 * 60_000,
    retry: odataRetry,
    queryFn: async (): Promise<UtDicts> => {
      const failed: string[] = []
      const load = async (path: string, ttl: number, label: string) => {
        try {
          return await fetchDict(qc, path, ttl)
        } catch (e) {
          if (e instanceof Unauthorized) throw e
          failed.push(label)
          return null
        }
      }
      const [wh, deals, users] = await Promise.all([
        load(WAREHOUSES_PATH, 6 * 60 * 60_000, 'склады'),
        load(UT_DEALS_PATH, 15 * 60_000, 'сделки 1С'),
        load(UT_USERS_PATH, 60 * 60_000, 'менеджеры 1С'),
      ])
      const names = emptyUtNames()
      if (wh) names.warehouse = refMap(wh.rows)
      if (deals) names.deal = refMap(deals.rows)
      if (users) names.user = refMap(users.rows)
      return { names, failed, meta: mergeMeta([wh?.meta, deals?.meta, users?.meta]) }
    },
  })
}

/**
 * Только склады и менеджеры — без справочника сделок 1С.
 *
 * Отдельный хук, а не параметр у useUtDicts, потому что это про РАЗНЫЕ потребности, и
 * различие стоит денег. «Кабинет закупщика» читает документы 1С через прокси, и номер
 * сделки Bitrix ему взять больше неоткуда — только из Description справочника сделок
 * (1435 записей на 28.07.2026, живут 15 минут, то есть запрашиваются заново несколько
 * раз в час).
 * Разделу «Отгрузка» этот справочник больше не нужен вовсе: номер сделки приходит уже
 * разобранным из зеркала (ut_deals.bitrix_deal_id), и тянуть полторы тысячи строк ради
 * того, что уже есть в ответе ядра, — чистая трата.
 *
 * Пути складов и пользователей совпадают до символа с теми, что просит useUtDicts, значит
 * у react-query это ТОТ ЖЕ ключ кэша: два экрана делят один запрос, а не делают два.
 */
export function useUtRefNames() {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['ut-ref-names'],
    staleTime: 30 * 60_000,
    retry: odataRetry,
    queryFn: async (): Promise<UtDicts> => {
      const failed: string[] = []
      const load = async (path: string, ttl: number, label: string) => {
        try {
          return await fetchDict(qc, path, ttl)
        } catch (e) {
          if (e instanceof Unauthorized) throw e
          failed.push(label)
          return null
        }
      }
      const [wh, users] = await Promise.all([
        load(WAREHOUSES_PATH, 6 * 60 * 60_000, 'склады'),
        load(UT_USERS_PATH, 60 * 60_000, 'менеджеры 1С'),
      ])
      const names = emptyUtNames()
      if (wh) names.warehouse = refMap(wh.rows)
      if (users) names.user = refMap(users.rows)
      return { names, failed, meta: mergeMeta([wh?.meta, users?.meta]) }
    },
  })
}

/**
 * Названия контрагентов и партнёров для ПОКАЗАННОЙ страницы. Их в 1С по 6900 — целиком
 * не тянем; пачка — 12 ссылок на запрос (длинный OR-фильтр 1С отвергает как «URI too long»,
 * это найдено на бою в портале 55).
 */
export function useUtNamesForPage(contragents: string[], partners: string[]) {
  // ключ — отсортированный список ссылок: при листании он меняется, а при перерисовке нет
  const key = useMemo(() => ({
    co: [...new Set(contragents.filter(Boolean))].sort(),
    pa: [...new Set(partners.filter(Boolean))].sort(),
  }), [contragents, partners])

  return useQuery({
    queryKey: ['ut-page-names', key],
    enabled: key.co.length > 0 || key.pa.length > 0,
    staleTime: 15 * 60_000,
    retry: odataRetry,
    queryFn: async (): Promise<{ contragent: Record<string, string>; partner: Record<string, string> }> => {
      const grab = async (entity: string, refs: string[]) => {
        const parts = await Promise.all(refNamePaths(entity, refs).map(async (p) => {
          try {
            return (await odataGet<RefName>('ut', p)).rows
          } catch (e) {
            if (e instanceof Unauthorized) throw e
            return []            // название — не повод ронять таблицу
          }
        }))
        return refMap(parts.flat())
      }
      const [contragent, partner] = await Promise.all([
        grab('Catalog_Контрагенты', key.co),
        grab('Catalog_Партнеры', key.pa),
      ])
      return { contragent, partner }
    },
  })
}

export interface ShippingDocs {
  orders: UtOrderRaw[]
  shipments: UtShipmentRaw[]
  ordersInWindow: number
  shipmentsInWindow: number
  truncated: boolean
  meta?: OdataMeta
}

/**
 * Заказы клиента и реализации за окно — одним хуком, ПРЯМО ИЗ 1С через прокси.
 *
 * ЗАПАСНОЙ ПУТЬ, а не основной. С 27.07.2026 раздел «Отгрузка» читает зеркало документов
 * через ручку ядра /ut/shipping (api/ut.ts): там постраничность, фильтры и итоги считает
 * Postgres по всей базе, а не браузер по загруженному окну. Хук оставлен и работает,
 * потому что он отвечает на другой вопрос — «что в 1С ПРЯМО СЕЙЧАС», без задержки на синк
 * (он бегает раз в несколько минут). Это нужно в двух случаях: когда синк 1С встал или
 * отстал, и когда сверяют зеркало с источником. Удалять его молча нельзя: тогда единственный
 * способ увидеть документ, которого ещё нет в зеркале, — идти в саму 1С.
 *
 * Почему заказы и реализации вместе: «ждёт отгрузки» — это заказ, на который НЕТ реализации,
 * и по этому пути посчитать его можно, только имея оба списка. Отсюда и его известное
 * ограничение: считается по окну, а не по базе (см. pendingOrders).
 */
export function useShippingDocs(days: number) {
  return useQuery({
    queryKey: ['ut-shipping', days],
    retry: odataRetry,
    // Ядро держит документы 300 с; свой кэш чуть короче, чтобы «обновить» шло в ядро.
    staleTime: 120_000,
    placeholderData: (prev: ShippingDocs | undefined) => prev,
    queryFn: async (): Promise<ShippingDocs> => {
      const [orders, shipments] = await Promise.all([
        fetchDocs<UtOrderRaw>(docWindowPath('Document_ЗаказКлиента', days, ORDER_SELECT)),
        fetchDocs<UtShipmentRaw>(docWindowPath('Document_РеализацияТоваровУслуг', days, SHIPMENT_SELECT)),
      ])
      return {
        orders: orders.rows,
        shipments: shipments.rows,
        ordersInWindow: orders.totalInWindow,
        shipmentsInWindow: shipments.totalInWindow,
        truncated: orders.truncated || shipments.truncated,
        meta: mergeMeta([orders.meta, shipments.meta]),
      }
    },
  })
}

export interface SupplyDocs {
  rows: UtSupplierOrderRaw[]
  inWindow: number
  truncated: boolean
  meta?: OdataMeta
}

/** Заказы поставщику за окно — вторая половина работы закупщика: что заказано и что пришло. */
export function useSupplierOrders(days: number) {
  return useQuery({
    queryKey: ['ut-supply', days],
    retry: odataRetry,
    staleTime: 120_000,
    placeholderData: (prev: SupplyDocs | undefined) => prev,
    queryFn: async (): Promise<SupplyDocs> => {
      const r = await fetchDocs<UtSupplierOrderRaw>(
        docWindowPath('Document_ЗаказПоставщику', days, SUPPLIER_SELECT),
      )
      return { rows: r.rows, inWindow: r.totalInWindow, truncated: r.truncated, meta: r.meta }
    },
  })
}

/**
 * Поиск контрагента по названию или ИНН → его ссылки. Нужен, чтобы искать отгрузки по
 * клиенту: в самих документах лежит только guid контрагента, а названий 6900 — искать
 * по ним локально не получится, не выкачав весь справочник.
 */
export function useContragentSearch(q: string) {
  const path = contragentSearchPath(q)
  return useQuery({
    queryKey: ['ut-contragent-search', path],
    enabled: !!path,
    staleTime: 5 * 60_000,
    retry: odataRetry,
    queryFn: async (): Promise<{ refs: string[]; names: Record<string, string> }> => {
      const r = await odataGet<RefName>('ut', path!)
      return { refs: r.rows.map((x) => x.Ref_Key).filter(Boolean), names: refMap(r.rows) }
    },
  })
}
